import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TicketOrg {
  id: string;
  name: string;
  plan: string | null;
  plan_status: string | null;
}

interface TicketUser {
  id: string;
  name: string;
  email: string;
}

interface SupportTicket {
  id: string;
  ticket_number: string | null;
  subject: string;
  body: string;
  attachments: string[];
  status: 'open' | 'in_progress' | 'closed';
  created_at: string;
  organization: TicketOrg | null;
  user: TicketUser | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function planBadgeClass(plan: string | null): string {
  switch (plan) {
    case 'gold':     return 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200';
    case 'platinum': return 'bg-purple-100 text-purple-700 hover:bg-purple-100 border-purple-200';
    case 'silver':   return 'bg-gray-100 text-gray-600 hover:bg-gray-100 border-gray-200';
    default:         return 'bg-blue-100 text-blue-700 hover:bg-blue-100 border-blue-200';
  }
}


// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchTickets(page: number, orgName: string, plan: string, status: string) {
  const res = await apiClient.get('/support-tickets', {
    params: {
      page,
      per_page: 20,
      org_name:  orgName  || undefined,
      plan:      plan     || undefined,
      status:    status   || undefined,
    },
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// AdminSupportPage
// ---------------------------------------------------------------------------

export default function AdminSupportPage() {
  const [page, setPage]             = useState(1);
  const [orgNameInput, setOrgNameInput] = useState('');
  const [orgName, setOrgName]       = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-support-tickets', page, orgName, planFilter],
    queryFn: () => fetchTickets(page, orgName, planFilter, ''),
  });

  const tickets: SupportTicket[] = data?.data?.tickets ?? [];
  const meta = data?.meta;
  const lastPage = meta?.last_page ?? 1;
  const total = meta?.total ?? 0;

  const handleSearch = () => {
    setOrgName(orgNameInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Support Requests</h1>
          <p className="text-sm text-muted-foreground">
            All support tickets submitted by organizations ({total} total)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Filter by organization name…"
            value={orgNameInput}
            onChange={(e) => setOrgNameInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>
          Search
        </Button>

        {/* Plan filter */}
        <div className="w-40">
          <Select
            value={planFilter || '_all'}
            onValueChange={(v) => { setPlanFilter(v === '_all' ? '' : v); setPage(1); }}
          >
            <SelectTrigger>
              <SelectValue placeholder="All Plans" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Plans</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="platinum">Platinum</SelectItem>
            </SelectContent>
          </Select>
        </div>

      </div>

      {/* Table */}
      <div className="rounded-xl border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Ticket #</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Subject</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Organization</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Submitted by</th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td>
              </tr>
            ) : tickets.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No support requests found.</td>
              </tr>
            ) : (
              tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  className="hover:bg-muted/20 transition-colors cursor-pointer"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <td className="px-4 py-3 font-mono font-semibold text-sky-600 text-sm whitespace-nowrap">
                    {ticket.ticket_number ?? '—'}
                  </td>
                  <td className="px-4 py-3 font-medium max-w-xs truncate" title={ticket.subject}>
                    {ticket.subject}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium">
                        {ticket.organization?.name ?? <span className="italic text-xs text-muted-foreground">Unknown</span>}
                      </span>
                      {ticket.organization?.plan && (
                        <Badge className={`w-fit text-xs capitalize ${planBadgeClass(ticket.organization.plan)}`}>
                          {ticket.organization.plan}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <div className="flex flex-col">
                      <span>{ticket.user?.name ?? '—'}</span>
                      <span className="text-xs">{ticket.user?.email ?? ''}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {new Date(ticket.created_at).toLocaleDateString('en-CA', {
                      year: 'numeric', month: 'short', day: 'numeric',
                    })}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {lastPage > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t text-sm text-muted-foreground">
            <span>Page {page} of {lastPage}</span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Ticket detail modal */}
      {selectedTicket && (
        <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="pr-8">{selectedTicket.subject}</DialogTitle>
            </DialogHeader>

            {/* Meta info */}
            <div className="flex flex-wrap gap-3 text-sm pb-3 border-b">
              {selectedTicket.ticket_number && (
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Ticket #:</span>
                  <span className="font-mono font-semibold text-sky-600">{selectedTicket.ticket_number}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Organization:</span>
                <span className="font-medium">{selectedTicket.organization?.name ?? '—'}</span>
                {selectedTicket.organization?.plan && (
                  <Badge className={`text-xs capitalize ${planBadgeClass(selectedTicket.organization.plan)}`}>
                    {selectedTicket.organization.plan}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">From:</span>
                <span>{selectedTicket.user?.name} ({selectedTicket.user?.email})</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Date:</span>
                <span>{new Date(selectedTicket.created_at).toLocaleString('en-CA')}</span>
              </div>
            </div>

            {/* Body */}
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: selectedTicket.body }}
            />

            {/* Attachments */}
            {selectedTicket.attachments?.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Attachments ({selectedTicket.attachments.length})</p>
                <div className="flex flex-wrap gap-2">
                  {selectedTicket.attachments.map((url, idx) => (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 underline break-all"
                    >
                      Attachment {idx + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
